/* eslint-disable no-unused-vars */
/**
 * AlertDashboard Component
 *
 * Este componente muestra un panel de alertas que incluye:
 * - Un resumen de alertas por nivel de severidad.
 * - Una tabla interactiva para visualizar mensajes de alerta.
 *
 * Se obtienen los datos de alertas del usuario a través de Redux y se actualizan al montar el componente.
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { attendAlert, getUserAlerts } from 'js/actions/userAlertsAction';

import { Box, Grid2, Typography } from '@mui/material';
import { Dashboard as DashboardLayout } from 'layouts';
import { useNavigate, useParams } from 'react-router-dom';
import { Card } from 'components';
import {
  AssignmentLate,
  Clear,
  PriorityHigh,
  Warning,
} from '@mui/icons-material';
import { getColumns } from './columns';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';

/**
 * QuickSearchToolbar
 *
 * Componente para agregar una barra de búsqueda rápida a la tabla de alertas.
 */
function QuickSearchToolbar() {
  return (
    <Box
      sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'flex-end', // Alinea el filtro a la derecha
      }}
    >
      <GridToolbarQuickFilter /> {/* Barra de búsqueda rápida para la tabla */}
    </Box>
  );
}

/**
 * AlertDashboard
 *
 * Componente principal que muestra el panel de alertas.
 *
 * - Recupera alertas del usuario al montar el componente.
 * - Muestra tarjetas con conteo de alertas por nivel de severidad.
 * - Renderiza una tabla interactiva con los mensajes de alerta.
 */
export default function AlertDashboard() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const params = useParams();
  const { chargeId, groupId } = params;
  const { userAlerts } = useSelector((state) => state.userAlert);

  const markAttended = (alert) => {
    dispatch(attendAlert(alert.id, chargeId));
  };

  const columns = getColumns(markAttended, intl);

  useEffect(() => {
    dispatch(getUserAlerts(chargeId));
  }, [dispatch, chargeId]);

  return (
    <DashboardLayout>
      <Grid2 container={true} spacing={4} mt={4} mr={3}>
        {/* Tarjetas de resumen de alertas */}
        {[
          {
            level: 1,
            color: '#0086c0',
            icon: <PriorityHigh />,
            label: intl.formatMessage({
              id: 'app.alerts.header.lowLevelIssues',
            }),
          },
          {
            level: 2,
            color: '#fdab3d',
            icon: <Warning />,
            label: intl.formatMessage({
              id: 'app.alerts.header.mediumLevelIssues',
            }),
          },
          {
            level: 3,
            color: '#e2445c',
            icon: <Clear />,
            label: intl.formatMessage({
              id: 'app.alerts.header.highLevelIssues',
            }),
          },
        ].map(({ level, color, icon, label }) => (
          <Grid2 key={level} size={3}>
            <Card
              title={<Typography variant="h5">{label}</Typography>}
              data={
                <Typography variant="h3">
                  {userAlerts[`alert_lvl_${level}_count`] || 0}
                </Typography>
              }
              footer={' '}
              icon={
                <div
                  style={{
                    backgroundColor: color,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: '50%',
                    color: 'white',
                  }}
                >
                  {icon}
                </div>
              }
            />
          </Grid2>
        ))}
        <Grid2 size={3}>
          <Card
            title={intl.formatMessage({ id: 'app.alerts.grid.title' })}
            data=" "
            bgColor="#840c74"
            footer={' '}
            link={`/alerts/${groupId}/charge/${chargeId}`}
            clicked={true}
            icon={<AssignmentLate sx={{ color: '#840c74' }} />}
          />
        </Grid2>
        {/* Tabla de alertas */}
        <Grid2 size={12} height={500}>
          <DataGrid
            columns={columns}
            rows={userAlerts.messages}
            getRowId={(row) => row.id}
            rowSelection={false}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            slots={{ toolbar: QuickSearchToolbar }}
            sx={{
              pt: 2,
              pb: 2,
              borderRadius: 2,
              boxShadow: 3,
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              '& .MuiDataGrid-root': { border: 'none' },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid rgba(224, 224, 224, 1)',
                maxHeight: '60px',
              },
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid rgb(199, 199, 199)',
              },
              '& .MuiDataGrid-row': {
                borderBottom: '1px solid rgb(199, 199, 199)',
                maxHeight: '60px',
              },
            }}
          />
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
}
